import { useState } from 'react'
import type { Dispatch, FC, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { textEntryIntentProps } from '@messenger/utils'
import {
  EDITOR_FIELD_CATALOG,
  fieldKeyToDateColumn,
  fieldKeyToWritableCaseField,
  editorTextBoxFieldLabel,
  resolveEditorField,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCaseData,
  EditorSelection,
  EditorTextBoxMount,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import CalendarShell from '@/components/global/Calendar/Calendar'
import {
  AuthoringFooter,
  AuthoringModalFrame,
  seedFieldRun,
  useAuthoringCommit,
} from './EditorTextBoxAuthoringModal'
import type { FieldAddress } from './EditorTextBoxAuthoringModal'
import * as S from './EditorTextBoxModal.styled'

const Calendar = CalendarShell as unknown as FC<{
  date: [Date | null, Dispatch<SetStateAction<Date | null>>]
  show: [boolean, (next: boolean) => void]
}>

type AuthorValue = (fieldKey: string, value: string | Date) => Promise<boolean>

type DateSupport = {
  getRawDate: (column: string) => Date | null
}

type FieldInsertModalProps = {
  getMount: () => EditorTextBoxMount | null
  getHostEl: () => HTMLDivElement | null
  caseData?: EditorCaseData
  savedSel?: EditorSelection | null
  fieldKey: string
  /** The field run the modal was opened on; unset, the box's first run of the key. */
  field?: FieldAddress | null
  onAuthorValue?: AuthorValue
  dateSupport?: DateSupport | null
  onClose: () => void
}

const fieldIsMultiline = (key: string): boolean =>
  !!EDITOR_FIELD_CATALOG.find((f) => f.key === key)?.multiline

const FieldInsertModal = ({
  getMount,
  getHostEl,
  caseData = {},
  savedSel = null,
  fieldKey,
  field = null,
  onAuthorValue,
  dateSupport = null,
  onClose,
}: FieldInsertModalProps) => {
  const { t } = useTranslation()
  const label = editorTextBoxFieldLabel(t, fieldKey)
  const caseValue = resolveEditorField(fieldKey, caseData)
  const writable = fieldKeyToWritableCaseField(fieldKey)
  const canAuthorValue = !!writable && typeof onAuthorValue === 'function'
  const multiline = fieldIsMultiline(fieldKey)
  const isFuneralHomeField = fieldKey.startsWith('fh:')
  // The printed-in line is computed from the order's theme and the funeral
  // home's country (`printedInLine`), so no case-details field feeds it.
  const isPrintedInField = fieldKey === 'printed_in'

  const dateColumn = fieldKeyToDateColumn(fieldKey)
  const isDateField = !!dateColumn
  const canAuthorDate = isDateField && typeof onAuthorValue === 'function'
  const [initialDate] = useState<Date | null>(() =>
    isDateField && dateColumn
      ? (dateSupport?.getRawDate?.(dateColumn) ?? null)
      : null,
  )
  const [pickedDate, setPickedDate] = useState<Date | null>(initialDate)
  const dateTime = (d: Date | null): number | null =>
    d instanceof Date ? d.getTime() : null
  const dateDirty =
    isDateField && dateTime(pickedDate) !== dateTime(initialDate)

  const [seed] = useState<FieldAddress | null>(() => {
    const existing = seedFieldRun(getMount(), fieldKey, field)
    return existing
      ? { blockIndex: existing.blockIndex, runIndex: existing.runIndex }
      : null
  })

  const [value, setValue] = useState(caseValue)
  const { saving, error, run } = useAuthoringCommit()

  const editMode = seed !== null

  const valueDirty = value !== caseValue

  const restoreCaret = () => {
    getHostEl()?.focus()
    getMount()?.placeCaretAtPos(savedSel?.focus ?? { blockIndex: 0, offset: 0 })
  }
  const close = () => {
    restoreCaret()
    onClose()
  }

  const commitValue = async (): Promise<boolean> => {
    if (isDateField) {
      if (!canAuthorDate || !dateDirty) return true
      return run(
        () => onAuthorValue!(fieldKey, pickedDate!),
        t('editor.textBox.modals.field.saveDateError'),
      )
    }
    if (!canAuthorValue || !valueDirty) return true
    return run(
      () => onAuthorValue!(fieldKey, value),
      t('editor.textBox.modals.field.saveValueError'),
    )
  }

  const insertField = async () => {
    if (!(await commitValue())) return
    getHostEl()?.focus()
    if (savedSel) getMount()?.placeRangeAtPos(savedSel.anchor, savedSel.focus)
    getMount()?.insertField(fieldKey)
    onClose()
  }

  const saveField = async () => {
    if (!(await commitValue())) return
    close()
  }

  const savable = isDateField ? canAuthorDate : canAuthorValue
  const dirty = isDateField ? dateDirty : valueDirty

  const convertField = () => {
    if (seed) getMount()?.convertFieldToText(seed.blockIndex, seed.runIndex)
    close()
  }

  const valueHint = (
    <S.Hint data-editor-text-fm-hint>
      {isDateField
        ? t('editor.textBox.modals.field.hintDate')
        : canAuthorValue
          ? t('editor.textBox.modals.field.hintEditable')
          : isFuneralHomeField
            ? t('editor.textBox.modals.field.hintFuneralHome')
            : isPrintedInField
              ? t('editor.textBox.modals.field.hintPrintedIn')
              : t('editor.textBox.modals.field.hintReadOnly')}
    </S.Hint>
  )

  return (
    <AuthoringModalFrame
      backdropTestId="data-editor-text-field-modal-backdrop"
      onDismiss={close}
      dialogAttrs={{ 'data-editor-text-field-modal': true }}
      ariaLabel={label}
      title={label}
      subtitle={
        editMode
          ? t('editor.textBox.modals.field.subtitleEdit')
          : t('editor.textBox.modals.common.subtitleInsert')
      }
      closeAttrs={{ 'data-editor-text-field-close': true }}
      footer={
        <AuthoringFooter
          danger={
            editMode ? (
              <S.DangerBtn
                type="button"
                data-editor-text-field-convert
                title={t('editor.textBox.modals.field.convertTitle')}
                onClick={convertField}
              >
                {t('editor.textBox.modals.common.convertToText')}
              </S.DangerBtn>
            ) : null
          }
          cancelAttrs={{ 'data-editor-text-field-cancel': true }}
          onCancel={close}
          secondary={
            editMode || !savable
              ? null
              : {
                  attrs: { 'data-editor-text-field-save-only': true },
                  label: t('editor.textBox.modals.common.saveToCase'),
                  title: dirty
                    ? t('editor.textBox.modals.field.saveOnlyTitle')
                    : t('editor.textBox.modals.field.unchangedTitle'),
                  disabled: !dirty,
                  onClick: saveField,
                }
          }
          primaryAttrs={
            editMode
              ? { 'data-editor-text-field-save': true }
              : { 'data-editor-text-field-insert': true }
          }
          primaryLabel={
            editMode
              ? t('editor.textBox.modals.common.saveChanges')
              : t('editor.textBox.modals.common.insertField')
          }
          onPrimary={editMode ? saveField : insertField}
          saving={saving}
        />
      }
    >
      <S.Section>
        <S.SectionLabel>
          {t('editor.textBox.modals.common.value')}
        </S.SectionLabel>
        {isDateField ? (
          <div data-editor-text-fm-datepicker>
            <Calendar
              date={[pickedDate, setPickedDate]}
              show={[true, () => {}]}
            />
          </div>
        ) : !canAuthorValue ? (
          <S.ReadOnlyValue data-editor-text-fm-readonly-value>
            {caseValue === '' ? '—' : caseValue}
          </S.ReadOnlyValue>
        ) : multiline ? (
          <S.ValueTextarea
            data-editor-text-fm-value
            {...textEntryIntentProps()}
            value={value}
            placeholder={t('editor.textBox.modals.field.placeholder', {
              label,
            })}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <S.ValueInput
            data-editor-text-fm-value
            {...textEntryIntentProps()}
            type="text"
            value={value}
            placeholder={t('editor.textBox.modals.field.placeholder', {
              label,
            })}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
                if (editMode) saveField()
                else insertField()
              }
            }}
          />
        )}
        {valueHint}
        {error && (
          <S.ErrorText data-editor-text-field-error>{error}</S.ErrorText>
        )}
      </S.Section>
    </AuthoringModalFrame>
  )
}

export default FieldInsertModal
